/* eslint-disable @typescript-eslint/no-explicit-any */
import { BoxProps as ChakraBoxProps } from '@chakra-ui/react';
import { forwardRef } from '@chakra-ui/system';
import { getCoreRowModel, getSortedRowModel, useReactTable, Table, SortingState } from '@tanstack/react-table';
import React, { useContext, createContext, useState, useEffect, useRef } from 'react';

import { TableProps } from './TableTypes';

function checkScrollLeft(element: HTMLElement) {
  return element.scrollLeft === 0;
}

export type TableExperimentalContext = TableProps<any> & {
  isScrollLeftEdge: boolean;
  isRowSelectionEnabled: boolean;
  boxProps: ChakraBoxProps;
  reactTable: Table<any>;
  ref: React.MutableRefObject<HTMLTableElement>;
};

export const TableExperimentalContext = createContext<TableExperimentalContext | null>(null);

export const TableExperimentalContextProvider = forwardRef(
  (props: TableProps<any>, ref: React.MutableRefObject<HTMLTableElement>): React.ReactElement => {
    const {
      data,
      columns,
      onRowClick,
      onRowSelectionChange,
      onSortingChange,
      enableSorting,
      defaultSorting,
      disableRowHoverStyle,
      isStickyHeader,
      isStickyFirstColumn,
      sx,
      children,
      ...rest
    } = props;
    const newRef = useRef<HTMLElement>(null);
    const componentRef = ref || newRef;
    const [isScrollLeftEdge, setIsScrollLeftEdge] = useState(false);
    const [rowSelection, setRowSelection] = useState({});
    const isRowSelectionEnabled = onRowSelectionChange !== undefined;
    const [sorting, setSorting] = React.useState<SortingState>(defaultSorting || []);
    const [didMount, setDidMount] = useState(false);

    const table = useReactTable({
      columns,
      data,
      state: {
        rowSelection,
        sorting
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onRowSelectionChange: setRowSelection,
      enableSorting
    });

    useEffect(() => {
      setDidMount(true);
    }, []);

    useEffect(() => {
      const element = componentRef.current;
      const scrollListener = () => {
        setIsScrollLeftEdge(checkScrollLeft(element));
      };

      setIsScrollLeftEdge(checkScrollLeft(element));
      element.addEventListener('scroll', scrollListener);

      return () => {
        element.removeEventListener('scroll', scrollListener);
      };
    }, [componentRef]);

    useEffect(() => {
      // avoid unnecessary first call of onRowSelectionChange when mounting
      if (!didMount) {
        return;
      }

      if (onRowSelectionChange) {
        onRowSelectionChange(table.getSelectedRowModel().flatRows);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table, rowSelection, onRowSelectionChange]);

    useEffect(() => {
      // avoid unnecessary first call of onSortingChange when mounting
      if (!didMount) {
        return;
      }

      if (enableSorting && onSortingChange) {
        onSortingChange(sorting);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting, enableSorting, onSortingChange]);

    const boxProps = {
      ...rest
    };

    const value = {
      data,
      columns,
      onRowClick,
      onRowSelectionChange,
      enableSorting,
      disableRowHoverStyle,
      isStickyHeader,
      isStickyFirstColumn,
      isScrollLeftEdge,
      isRowSelectionEnabled,
      boxProps,
      reactTable: table,
      sx,
      ref: componentRef,
      ...rest
    };

    return <TableExperimentalContext.Provider value={value}>{children}</TableExperimentalContext.Provider>;
  }
);

export const useTableExperimentalContext = (): TableExperimentalContext => {
  const context = useContext(TableExperimentalContext);

  if (context === null) {
    throw new Error('TableExperimental components must be used within a TableExperimentalContextProvider.');
  }

  return context;
};
